import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowUp, FileText, MessageCircle, Paperclip, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";

const MAX_MB = 20;

const Attachment = ({ m, a, mine }) => {
  const url = fileUrl(`/api/messages/${m.id}/files/${a.id}`);
  if (a.content_type?.startsWith("image/") && !a.content_type.includes("hei")) {
    return <a href={url} target="_blank" rel="noreferrer" data-testid={`chat-att-${a.id}`}><img src={url} alt={a.filename} loading="lazy" className="h-36 w-36 object-cover rounded-xl" /></a>;
  }
  return (
    <a href={url} target="_blank" rel="noreferrer" className={`flex items-center gap-2 rounded-xl px-3 py-2 text-sm underline ${mine ? "bg-white/10" : "bg-[#F7F7F7]"}`} data-testid={`chat-att-${a.id}`}>
      <FileText className="h-4 w-4 shrink-0" /><span className="truncate max-w-[200px]">{a.filename}</span>
    </a>
  );
};

const Bubble = ({ m, mine }) => (
  <div className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid="chat-message">
    <div className={`max-w-[80%] rounded-[20px] px-4 py-3 ${mine ? "bg-[#070707] text-white rounded-br-md" : "bg-white border border-[#EBEBEB] rounded-bl-md"}`}>
      {!mine && <p className="text-[11px] font-semibold uppercase tracking-wider text-[#757575] mb-1">{m.sender_name}</p>}
      {m.text && <p className="text-[15px] whitespace-pre-wrap leading-relaxed">{m.text}</p>}
      {m.attachments?.length > 0 && <div className="flex flex-wrap gap-2 mt-2" data-testid="chat-attachments">{m.attachments.map((a) => <Attachment key={a.id} m={m} a={a} mine={mine} />)}</div>}
      <p className={`text-[11px] mt-1.5 ${mine ? "text-white/50" : "text-[#757575]"}`}>{fmtDateTime(m.created_at)}</p>
    </div>
  </div>
);

export const ChatThread = ({ clientId, myRole, height = "h-[62vh]" }) => {
  const [msgs, setMsgs] = useState(null);
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const box = useRef(null);

  const load = useCallback(() => {
    api.get("/messages", { params: clientId ? { client_id: clientId } : {} }).then((r) => setMsgs(r.data)).catch(() => {});
  }, [clientId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (box.current) box.current.scrollTop = box.current.scrollHeight;
  }, [msgs?.length]);

  const pick = (e) => {
    const list = [...(e.target.files || [])];
    e.target.value = "";
    const big = list.filter((f) => f.size > MAX_MB * 1024 * 1024);
    if (big.length) toast.error(`File troppo grandi (max ${MAX_MB} MB): ${big.map((f) => f.name).join(", ")}`);
    setFiles((f) => [...f, ...list.filter((x) => x.size <= MAX_MB * 1024 * 1024)].slice(0, 10));
  };

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() && !files.length) return;
    setSending(true);
    try {
      let data;
      if (files.length) {
        const fd = new FormData();
        fd.append("text", text);
        if (clientId) fd.append("client_id", clientId);
        files.forEach((f) => fd.append("files", f));
        ({ data } = await api.post("/messages/upload", fd));
      } else {
        ({ data } = await api.post("/messages", { text, ...(clientId ? { client_id: clientId } : {}) }));
      }
      setMsgs((m) => [...(m || []), data]);
      setText("");
      setFiles([]);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={`rp-card flex flex-col ${height} overflow-hidden`} data-testid="chat-thread">
      <div ref={box} className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-3">
        {msgs && !msgs.length && (
          <div className="h-full flex flex-col items-center justify-center text-center text-[#757575] gap-2">
            <MessageCircle className="h-7 w-7 text-[#C8BDAB]" strokeWidth={1.5} />
            <p className="text-sm">Nessun messaggio. Scrivi il primo!</p>
          </div>
        )}
        {msgs?.map((m) => <Bubble key={m.id} m={m} mine={m.sender_role === myRole} />)}
      </div>
      {files.length > 0 && (
        <div className="px-3 pt-3 flex flex-wrap gap-2 bg-white border-t border-[#EBEBEB]" data-testid="chat-pending-files">
          {files.map((f, i) => (
            <span key={`${f.name}-${i}`} className="inline-flex items-center gap-1 rounded-full bg-[#F7F7F7] pl-3 pr-1 h-7 text-xs">
              <span className="truncate max-w-[160px]">{f.name}</span>
              <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} className="h-5 w-5 grid place-items-center rounded-full hover:bg-[#EBEBEB]" aria-label="Rimuovi" data-testid={`chat-file-remove-${i}`}><X className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
      )}
      <form onSubmit={send} className="p-3 border-t border-[#EBEBEB] bg-white flex gap-2">
        <label className="h-12 w-12 shrink-0 rounded-full border border-[#EBEBEB] flex items-center justify-center cursor-pointer hover:border-black transition-colors" aria-label="Allega foto o PDF" data-testid="chat-attach-button">
          <Paperclip className="h-5 w-5" />
          <input type="file" multiple accept="image/*,application/pdf" className="hidden" onChange={pick} data-testid="chat-file-input" />
        </label>
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Scrivi un messaggio…" className="rp-field rounded-full" data-testid="chat-input" />
        <button disabled={sending || (!text.trim() && !files.length)} data-testid="chat-send-button" className="h-12 w-12 shrink-0 rounded-full bg-[#070707] text-white flex items-center justify-center disabled:opacity-40 transition-opacity">
          <ArrowUp className="h-5 w-5" />
        </button>
      </form>
    </div>
  );
};
